/**
 * Why the embedding service gave no vectors (Phase 15g, D-253): codes from closed lists and numbers, never the
 * provider's own words. The gateway reads the provider's answer and names the reason from the list below
 * (model-gateway `vectorFailureReason`); the worker logs it and keeps it with the company's pause or with a
 * document's own next attempt; Yönetim → Onay politikaları → Belge araması shows it ("500 · Connection error").
 *
 * LiteLLM's and the model server's messages may repeat the input (a validation error quotes the request's text) or
 * a key's last characters, so the message never travels: only the reason chosen from it. `embeddingDiagnostics`
 * keeps a key only with a value from its list or range, whatever else a failure's details carry.
 */
export const EMBEDDING_FAILURE_REASONS = [
  // the provider answered with an error
  'connection_error',
  'request_timeout',
  'no_deployments',
  'rate_limited',
  'budget_exceeded',
  'service_unavailable',
  'bad_gateway',
  'gateway_timeout',
  'server_error',
  'auth_failed',
  'model_not_found',
  'input_too_long',
  'bad_request',
  'invalid_reply',
  // no answer came
  'timeout',
  'connection_refused',
  'host_not_found',
  'connection_reset',
  'tls_error',
  'unreachable',
  'blocked',
] as const;
export type EmbeddingFailureReason = (typeof EMBEDDING_FAILURE_REASONS)[number];

/**
 * A reason as the provider would say it (LiteLLM's own phrase where it has one), the same in every language: the
 * screen shows it after the status ("500 · Connection error") and the worker's log line uses it too, so an admin can
 * pass it on to whoever runs the proxy as it is.
 */
export const EMBEDDING_REASON_LABELS: Record<EmbeddingFailureReason, string> = {
  connection_error: 'Connection error',
  request_timeout: 'Request timed out',
  no_deployments: 'No deployments available',
  rate_limited: 'Too many requests',
  budget_exceeded: 'Budget exceeded',
  service_unavailable: 'Service unavailable',
  bad_gateway: 'Bad gateway',
  gateway_timeout: 'Gateway timeout',
  server_error: 'Internal server error',
  auth_failed: 'Authentication failed',
  model_not_found: 'Model not found',
  input_too_long: 'Input too long',
  bad_request: 'Bad request',
  invalid_reply: 'Invalid reply',
  timeout: 'Timed out',
  connection_refused: 'Connection refused',
  host_not_found: 'Host not found',
  connection_reset: 'Connection reset',
  tls_error: 'TLS error',
  unreachable: 'Unreachable',
  blocked: 'Address blocked',
};

/** What a failed embedding or rerank call may carry from the gateway to the worker (its error's `details`). */
export interface EmbeddingDiagnostics {
  /** the provider's HTTP status (500); absent when no answer came */
  status?: number;
  reason?: EmbeddingFailureReason;
  /** the gateway's tries for this call */
  attempts?: number;
}

const isReason = (value: unknown): value is EmbeddingFailureReason => typeof value === 'string' && (EMBEDDING_FAILURE_REASONS as readonly string[]).includes(value);
const intIn = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
const own = (source: object, key: string) => Object.prototype.hasOwnProperty.call(source, key);

/** The status, reason and tries of a failure's details; anything else, or a value off its list or range, is left out. */
export function embeddingDiagnostics(details: unknown): EmbeddingDiagnostics {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const source = details as Record<string, unknown>;
  const kept: EmbeddingDiagnostics = {};
  if (own(source, 'status') && intIn(source.status, 100, 599)) kept.status = source.status;
  if (own(source, 'reason') && isReason(source.reason)) kept.reason = source.reason;
  if (own(source, 'attempts') && intIn(source.attempts, 1, 20)) kept.attempts = source.attempts;
  return kept;
}

/** A stored reason read back: one from the list, or null. */
export function embeddingReason(value: unknown): EmbeddingFailureReason | null {
  return isReason(value) ? value : null;
}

/**
 * No answer at all (a refused connection, an unknown host, a timeout…), from the low-level error's code
 * (ECONNREFUSED, ENOTFOUND, UND_ERR_CONNECT_TIMEOUT…) and whether the call's own time ran out.
 */
export function connectionFailureReason(input: { cause?: string | null | undefined; timedOut?: boolean | undefined }): EmbeddingFailureReason {
  const cause = (input.cause ?? '').toUpperCase();
  if (input.timedOut || /TIMEOUT|TIMEDOUT/.test(cause)) return 'timeout';
  if (cause === 'ECONNREFUSED') return 'connection_refused';
  if (cause === 'ENOTFOUND' || cause === 'EAI_AGAIN' || cause === 'EAI_NONAME') return 'host_not_found';
  if (/ECONNRESET|EPIPE|ECONNABORTED|UND_ERR_SOCKET|UND_ERR_CLOSED/.test(cause)) return 'connection_reset';
  if (/CERT|SSL|TLS/.test(cause)) return 'tls_error';
  return 'unreachable';
}

/** What the screen says first (policies.documentVectors.problems.*): the gist of the gateway's code and the reason. */
export const EMBEDDING_PROBLEMS = ['not_answering', 'unreachable', 'auth', 'model', 'no_model', 'quota', 'blocked', 'gateway', 'rejected', 'invalid', 'other'] as const;
export type EmbeddingProblem = (typeof EMBEDDING_PROBLEMS)[number];

const NO_ANSWER: ReadonlySet<EmbeddingFailureReason> = new Set(['timeout', 'connection_refused', 'host_not_found', 'connection_reset', 'tls_error', 'unreachable']);

/**
 * The gist of a failure for the screen. `unavailable` is the worker's own code when the gateway itself does not
 * answer; `bad_request` is the provider refusing the text (a budget, a model name or a key it refuses is said so).
 */
export function embeddingProblem(code: string, reason?: EmbeddingFailureReason | null): EmbeddingProblem {
  switch (code) {
    case 'quota_exceeded':
      return 'quota';
    case 'provider_auth_failed':
      return 'auth';
    case 'model_not_found':
    case 'model_unavailable':
      return 'model';
    case 'no_model_configured':
      return 'no_model';
    case 'provider_url_blocked':
      return 'blocked';
    case 'unavailable':
    case 'internal_error':
      return 'gateway';
  }
  if (reason === 'budget_exceeded') return 'quota';
  if (reason === 'auth_failed') return 'auth';
  if (reason === 'model_not_found') return 'model';
  if (reason === 'blocked') return 'blocked';
  if (code === 'bad_request') return 'rejected';
  if (reason === 'invalid_reply') return 'invalid';
  if (reason && NO_ANSWER.has(reason)) return 'unreachable';
  if (code === 'provider_unavailable' || reason) return 'not_answering';
  return 'other';
}

/** One failure, as the worker's log line and Yönetim write it: "provider_unavailable · HTTP 500 · Connection error". */
export function describeEmbeddingFailure(code: string, diagnostics: EmbeddingDiagnostics): string {
  return [code, diagnostics.status ? `HTTP ${diagnostics.status}` : null, diagnostics.reason ? EMBEDDING_REASON_LABELS[diagnostics.reason] : null].filter(Boolean).join(' · ');
}

// ---- the API: Yönetim → Onay politikaları → Belge araması ------------------------------------------------------

/** A failure as the screen shows it. */
export interface EmbeddingFailureDto {
  /** the gateway's code (provider_unavailable, quota_exceeded…) */
  code: string;
  /** the provider's HTTP status (500), when it answered */
  status: number | null;
  reason: EmbeddingFailureReason | null;
  /** what the screen says first */
  problem: EmbeddingProblem;
  lastAttemptAt: string;
}

/**
 * The company's embeddings wait after failures every document would meet (the service does not answer, refuses the
 * key, has no such model…): no document calls it until `nextAttemptAt`, then one tries for all.
 */
export interface EmbeddingPauseDto extends EmbeddingFailureDto {
  /** failed attempts in a row */
  failures: number;
  /** the first failure of this pause */
  since: string;
  nextAttemptAt: string;
  /** a document is trying now */
  probing: boolean;
}

/** Documents waiting on their own (the provider refused their text, or the person's budget was used up). */
export interface EmbeddingRetriesDto extends EmbeddingFailureDto {
  count: number;
  /** the soonest of their next attempts */
  nextAttemptAt: string;
}

/**
 * GET /api/tenant/document-vectors (D-253), for the company's admins: why its documents have no vectors. Documents
 * without vectors are still searched by their words.
 */
export interface DocumentVectorsDto {
  /** the company's choice (D-58): with 'keyword' no document gets vectors, by design */
  documentSearch: 'hybrid' | 'keyword';
  /** the company's ready documents with text and no vectors in the current index, whoever may see them */
  waiting: number;
  /** null while the service works */
  pause: EmbeddingPauseDto | null;
  /** the latest failure among the documents waiting on their own; null when there is none */
  documents: EmbeddingRetriesDto | null;
}
